import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'

export function usePriceHistory(
  itemId:      string | null,
  householdId: string | null,
) {
  const { data: priceHistory = [], isLoading } = useQuery({
    queryKey: ['price-history', itemId],
    enabled:  !!itemId && !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('confirmations')
        .select('id, price_paid, confirmed_at, store_name, quantity')
        .eq('item_id', itemId!)
        .eq('household_id', householdId!)
        .not('price_paid', 'is', null)
        .order('confirmed_at', { ascending: true })
      return data ?? []
    },
  })

  const prices = priceHistory.map(r => r.price_paid as number)

  const avgPrice     = prices.length > 0 ? prices.reduce((s, p) => s + p, 0) / prices.length : null
  const lowestPrice  = prices.length > 0 ? Math.min(...prices) : null
  const highestPrice = prices.length > 0 ? Math.max(...prices) : null
  const latestPrice  = prices.length > 0 ? prices[prices.length - 1] : null
  const oldestPrice  = prices.length > 0 ? prices[0] : null

  const priceChangePct =
    oldestPrice != null && latestPrice != null && oldestPrice > 0
      ? ((latestPrice - oldestPrice) / oldestPrice) * 100
      : null

  // Group by month for chart
  const byMonth: Record<string, number[]> = {}
  for (const record of priceHistory) {
    const month = new Date(record.confirmed_at).toLocaleDateString('en-US', {
      month: 'short', year: '2-digit',
    })
    if (!byMonth[month]) byMonth[month] = []
    byMonth[month].push(record.price_paid as number)
  }

  const chartData = Object.entries(byMonth).map(([month, monthPrices]) => ({
    month,
    price: monthPrices.reduce((s, p) => s + p, 0) / monthPrices.length,
    count: monthPrices.length,
  }))

  return {
    priceHistory,
    chartData,
    avgPrice,
    lowestPrice,
    highestPrice,
    latestPrice,
    oldestPrice,
    priceChangePct,
    isLoading,
  }
}
